import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import {
    Home01Icon,
    BookOpen01Icon,
    UserGroupIcon,
    Analytics01Icon,
    Settings02Icon,
    Notification02Icon,
    Logout02Icon,
    VideoReplayIcon,
    PlayCircleIcon,
    DashboardSpeed01Icon,
    Menu01Icon,
    Cancel01Icon,
} from "@/components/ui/Icons";
import { cn } from "@/lib/utils";
import { ChevronDown, Moon, Sun } from "lucide-react";
import { AnimatedDropdownIcon, AnimatedMenuIcon } from "@/components/ui/AnimatedIcons";
import { Button } from "@/components/ui/button";
import { Calendar, MessageSquare, Wallet, FileText, Briefcase } from "lucide-react";
import { MobileHeader } from "@/components/layout/MobileHeader";
import { MobileDrawer } from "@/components/layout/MobileDrawer";
import api from "../pages/services/api";
import { useThemeStore } from "@/stores/themeStore";
import useAuthStore from "../stores/authStore";

// White-label: a custom logo (VITE_INSTITUTE_LOGO_URL) is used in BOTH themes
// with a dark-mode white chip; only the default logo swaps to /logo-dark.png.
const customLogo = import.meta.env.VITE_INSTITUTE_LOGO_URL;

const MENU_BY_ROLE = {
    admin: [
        { name: "Dashboard", href: "/admin", icon: Home01Icon },
        { name: "Attendance", href: "/admin/attendance", icon: Calendar },
        {
            name: "Calendar",
            icon: Calendar,
            children: [
                { name: "Schedule", href: "/admin/calendar", icon: Calendar },
                { name: "Availability", href: "/admin/availability", icon: Calendar },
            ],
        },
        {
            name: "Courses",
            icon: BookOpen01Icon,
            children: [
                { name: "Live Courses", href: "/admin/courses/live", icon: VideoReplayIcon },
                { name: "Group Courses", href: "/admin/courses/group", icon: UserGroupIcon },
                { name: "Recorded Courses", href: "/admin/courses/recorded", icon: PlayCircleIcon },
            ],
        },
        { name: "Users", href: "/admin/users", icon: UserGroupIcon },
        { name: "Chats", href: "/admin/chats", icon: MessageSquare },
        { name: "Resume", href: "/admin/resume", icon: FileText },
        { name: "Placements", href: "/admin/placements", icon: Briefcase },
        { name: "Analytics", href: "/admin/analysis", icon: Analytics01Icon },
    ],
    instructor: [
        // Attendance Portal removed for instructors — admin-only feature
        // (TODO #1). The mutation endpoints are already gated by isAdmin
        // server-side; removing the navigation entry eliminates the
        // confusion of seeing a portal you can't actually use.
        { name: "Dashboard", href: "/instructor", icon: DashboardSpeed01Icon },
        {
            name: "Calendar",
            icon: Calendar,
            children: [
                { name: "Schedule", href: "/instructor/calendar", icon: Calendar },
                { name: "Availability", href: "/instructor/availability", icon: Calendar },
            ],
        },
        {
            name: "Courses",
            // Recorded courses removed from instructor menu — recorded
            // content libraries don't have instructors per TODO #3.
            icon: BookOpen01Icon,
            children: [
                { name: "Group Courses", href: "/instructor/courses/group", icon: UserGroupIcon },
            ],
        },
        { name: "Assignments", href: "/instructor/assignment", icon: BookOpen01Icon },
        { name: "Payouts",     href: "/instructor/payouts",   icon: Wallet },
    ],
    learner: [
        { name: "Dashboard", href: "/learner", icon: Home01Icon },
        { name: "Calendar", href: "/learner/calendar", icon: Calendar },
        { name: "My Courses", href: "/learner/courses", icon: BookOpen01Icon },
        { name: "Chats", href: "/learner/chats", icon: MessageSquare },
        { name: "Attendance", href: "/learner/attendance", icon: Calendar },
        { name: "Fees", href: "/learner/fees", icon: Wallet },
    ],
};

export default function Navbar() {
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [userDropdownOpen, setUserDropdownOpen] = useState(false);
    const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
    const [features, setFeatures] = useState(null);

    // Use theme store
    const { isDark, toggleTheme } = useThemeStore();

    // Detect role from URL path as primary source (more reliable than localStorage)
    const getRoleFromPath = () => {
        const path = location.pathname;
        if (path.startsWith("/admin")) return "admin";
        if (path.startsWith("/instructor")) return "instructor";
        if (path.startsWith("/learner")) return "learner";
        return (localStorage.getItem("role") || "learner").toLowerCase();
    };

    const role = getRoleFromPath();
    const menuItems = useMemo(() => {
        const items = MENU_BY_ROLE[role] || MENU_BY_ROLE.learner;
        if (role === "learner" && features?.placement) {
            return [...items, { name: "Career", href: "/learner/career", icon: Briefcase }];
        }
        return items;
    }, [role, features]);
    const userName = localStorage.getItem("userName") || "User";
    const userInitial = userName.charAt(0).toUpperCase();

    const { logout } = useAuthStore();

    useEffect(() => {
        let mounted = true;

        const fetchUnreadCount = async () => {
            if (document.hidden) return;
            try {
                const { data } = await api.get("/notifications/unread-count");
                if (!mounted) return;
                setHasUnreadNotifications(Number(data?.unreadCount || 0) > 0);
            } catch {
                if (!mounted) return;
                setHasUnreadNotifications(false);
            }
        };

        fetchUnreadCount();
        const intervalId = setInterval(fetchUnreadCount, 30000);
        const onVisibility = () => { if (!document.hidden) fetchUnreadCount(); };
        document.addEventListener("visibilitychange", onVisibility);
        // Live push from Layout's socket subscriber — bypasses the 30s poll
        // so the badge ticks the moment a new notification row is inserted.
        const onLive = () => fetchUnreadCount();
        window.addEventListener("vr:notification", onLive);

        return () => {
            mounted = false;
            clearInterval(intervalId);
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("vr:notification", onLive);
        };
    }, [location.pathname]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") {
                setActiveDropdown(null);
                setUserDropdownOpen(false);
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);

    // Feature flags drive menu visibility (e.g. the learner Career tab ships dark).
    useEffect(() => {
        let mounted = true;
        api.get("/app-config")
            .then(({ data }) => { if (mounted) setFeatures(data?.data?.features || {}); })
            .catch(() => { if (mounted) setFeatures({}); });
        return () => { mounted = false; };
    }, []);

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    const isActive = (itemOrPath) => {
        const item = typeof itemOrPath === "string"
            ? { href: itemOrPath, exact: false }
            : (itemOrPath || {});

        const path = item.href || "";
        const exact = Boolean(item.exact);

        if (!path) return false;
        if (exact) return location.pathname === path;
        if (path === `/${role}`) return location.pathname === path;
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };

    return (
        <>
        <nav aria-label="Primary" className="hidden md:block sticky top-0 z-50 w-full bg-background/80 backdrop-blur-xl border-b border-sidebar-border">
            <div className="max-w-7xl mx-auto flex h-14 items-center px-4 lg:px-6 justify-between">
                <Link
                    to={`/${role}`}
                    aria-label="Home"
                    className="flex items-center gap-2.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                    {customLogo ? (
                        <img src={customLogo} alt="" className="h-12 w-12 object-contain dark:bg-white/90 dark:p-1 dark:rounded-md" />
                    ) : (
                        <>
                            <img src="/logo-square.png" alt="" className="h-12 w-12 object-contain dark:hidden" />
                            <img src="/logo-dark.png" alt="" className="h-12 w-12 object-contain hidden dark:block" />
                        </>
                    )}
                </Link>

                {/* DESKTOP MENU */}
                <div className="hidden md:flex items-center gap-1">
                    {menuItems.map((item, index) => {
                        const isOpen = activeDropdown === index;
                        return (
                            <div
                                key={item.name}
                                className="relative"
                                onMouseEnter={() => item.children && setActiveDropdown(index)}
                                onMouseLeave={() => setActiveDropdown(null)}
                            >
                                <Button
                                    variant="ghost"
                                    onClick={() => {
                                        if (item.children) {
                                            setActiveDropdown(isOpen ? null : index);
                                        } else {
                                            navigate(item.href);
                                        }
                                    }}
                                    aria-haspopup={item.children ? "menu" : undefined}
                                    aria-expanded={item.children ? isOpen : undefined}
                                    className={cn(
                                        "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                                        isActive(item) || isOpen
                                            ? "bg-primary/10 text-primary"
                                            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                                    )}
                                >
                                    <item.icon size={16} />
                                    <span>{item.name}</span>
                                    {item.children && (
                                        <ChevronDown
                                            size={14}
                                            className={cn("transition-transform", isOpen && "rotate-180")}
                                        />
                                    )}
                                </Button>

                                <AnimatePresence>
                                    {item.children && isOpen && (
                                        <motion.div
                                            role="menu"
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: 8 }}
                                            transition={{ duration: 0.15 }}
                                            className="absolute top-full left-0 mt-1 w-52 bg-card border border-border rounded-xl shadow-lg p-1.5 z-50"
                                        >
                                            {item.children.map((child) => (
                                                <Button
                                                    role="menuitem"
                                                    variant="ghost"
                                                    key={child.name}
                                                    onClick={() => {
                                                        navigate(child.href);
                                                        setActiveDropdown(null);
                                                    }}
                                                    className={cn(
                                                        "w-full flex items-center justify-start gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left group/item",
                                                        isActive(child)
                                                            ? "bg-primary text-primary-foreground"
                                                            : "text-foreground/80 hover:bg-muted"
                                                    )}
                                                >
                                                    <child.icon className={cn(
                                                        "h-4 w-4 shrink-0 transition-colors",
                                                        isActive(child) ? "text-primary-foreground" : "text-primary"
                                                    )} />
                                                    {child.name}
                                                </Button>
                                            ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        );
                    })}
                </div>

                <div className="flex items-center gap-1">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={toggleTheme}
                        aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                        aria-pressed={isDark}
                        className="h-9 w-9 text-muted-foreground hover:text-foreground"
                    >
                        {isDark ? <Sun size={18} /> : <Moon size={18} />}
                    </Button>

                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/${role}/notifications`)}
                        aria-label={hasUnreadNotifications ? "Notifications, unread" : "Notifications"}
                        className="relative h-9 w-9 text-muted-foreground hover:text-foreground"
                    >
                        <Notification02Icon size={18} />
                        {hasUnreadNotifications ? (
                            <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-destructive ring-2 ring-background" />
                        ) : null}
                    </Button>

                    <div
                        className="relative"
                        onMouseEnter={() => setUserDropdownOpen(true)}
                        onMouseLeave={() => setUserDropdownOpen(false)}
                    >
                        <Button
                            variant="ghost"
                            onClick={() => setUserDropdownOpen((v) => !v)}
                            aria-haspopup="menu"
                            aria-expanded={userDropdownOpen}
                            className="flex h-9 items-center gap-2 rounded-lg px-1.5 hover:bg-muted/50"
                        >
                            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                                {userInitial}
                            </div>
                            <span className="hidden text-sm font-medium text-foreground sm:block">
                                {userName}
                            </span>
                            <AnimatedDropdownIcon isOpen={userDropdownOpen} size={14} className="hidden text-muted-foreground sm:block" />
                        </Button>

                        <AnimatePresence>
                            {userDropdownOpen && (
                                <motion.div
                                    role="menu"
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 8 }}
                                    transition={{ duration: 0.15 }}
                                    className="absolute right-0 top-full z-50 mt-1 w-56 overflow-hidden rounded-xl border bg-card shadow-lg"
                                >
                                    <div className="border-b px-3 py-2.5">
                                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Signed in as</p>
                                        <p className="mt-0.5 truncate text-sm font-medium">{userName}</p>
                                    </div>
                                    <div className="p-1.5">
                                        <Button
                                            variant="ghost"
                                            onClick={() => {
                                                navigate(`/${role}/setting`);
                                                setUserDropdownOpen(false);
                                            }}
                                            className="flex w-full items-center justify-start gap-3 rounded-md px-3 py-2 text-sm font-normal text-foreground hover:bg-muted"
                                        >
                                            <Settings02Icon size={16} className="text-muted-foreground" />
                                            Settings
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            onClick={handleLogout}
                                            className="flex w-full items-center justify-start gap-3 rounded-md px-3 py-2 text-sm font-normal text-destructive hover:bg-destructive/10 hover:text-destructive"
                                        >
                                            <Logout02Icon size={16} />
                                            Sign out
                                        </Button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    <Button
                        variant="ghost"
                        size="icon"
                        aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                        aria-expanded={mobileMenuOpen}
                        className="h-9 w-9 md:hidden"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    >
                        <AnimatedMenuIcon isOpen={mobileMenuOpen} size={22} />
                    </Button>
                </div>
            </div>
        </nav>

        <MobileHeader 
            onMenuClick={() => setMobileMenuOpen(true)}
            role={role}
            userName={userName}
            userInitial={userInitial}
            hasUnreadNotifications={hasUnreadNotifications}
        />

        <MobileDrawer open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <div className="flex items-center justify-between p-4 border-b border-border">
                <span className="font-semibold text-lg">Menu</span>
                <Button
                    variant="ghost"
                    onClick={() => setMobileMenuOpen(false)}
                    className="p-1.5 rounded-lg hover:bg-muted min-h-[44px] min-w-[44px] flex items-center justify-center touch-target"
                >
                    <Cancel01Icon size={20} />
                </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-1 no-scrollbar momentum-scroll mobile-safe-bottom">
                {menuItems.map((item) => (
                    <div key={item.name}>
                        {item.children ? (
                            <div className="space-y-1">
                                <div className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-muted-foreground">
                                    <item.icon className="h-[18px] w-[18px] shrink-0 text-primary" />
                                    {item.name}
                                </div>
                                <div className="ml-4 pl-4 border-l border-border space-y-0.5">
                                    {item.children.map((child) => (
                                        <Button
                                            variant="ghost"
                                            key={child.name}
                                            className={cn(
                                                "w-full flex items-center justify-start gap-3 px-3 py-2 rounded-lg text-sm transition-colors text-left min-h-[44px] touch-target",
                                                isActive(child)
                                                    ? "bg-primary text-primary-foreground"
                                                    : "text-foreground/70 hover:bg-muted"
                                            )}
                                            onClick={() => {
                                                navigate(child.href);
                                                setMobileMenuOpen(false);
                                            }}
                                        >
                                            <child.icon className="h-4 w-4 shrink-0 text-primary" />
                                            {child.name}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <Button
                                variant="ghost"
                                className={cn(
                                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left min-h-[44px] touch-target",
                                    isActive(item)
                                        ? "bg-primary/10 text-primary"
                                        : "text-foreground hover:bg-muted"
                                )}
                                onClick={() => {
                                    navigate(item.href);
                                    setMobileMenuOpen(false);
                                }}
                            >
                                <item.icon size={18} />
                                {item.name}
                            </Button>
                        )}
                    </div>
                ))}
            </div>

            <div className="border-t border-border p-4 space-y-1 mobile-safe-bottom">
                <Button
                    variant="ghost"
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-muted transition-colors min-h-[44px] touch-target"
                    onClick={toggleTheme}
                >
                    {isDark ? <Sun size={18} /> : <Moon size={18} />}
                    {isDark ? "Light mode" : "Dark mode"}
                </Button>
                <Button
                    variant="ghost"
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-muted transition-colors min-h-[44px] touch-target"
                    onClick={() => {
                        navigate(`/${role}/setting`);
                        setMobileMenuOpen(false);
                    }}
                >
                    <Settings02Icon size={18} />
                    Settings
                </Button>
                <Button
                    variant="ghost"
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors min-h-[44px] touch-target"
                    onClick={handleLogout}
                >
                    <Logout02Icon size={18} />
                    Sign out
                </Button>
            </div>
        </MobileDrawer>
        </>);
}
